import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../../lib/formatting'
import {
  Upload,
  FileText,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  Trash2,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Tag as TagIcon,
  HardDriveUpload,
  AlertCircle,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import Modal from '../ui/Modal'
import AiResultModal from '../ui/AiResultModal'
import DocumentViewerModal from '../documents/DocumentViewerModal'
import DocIndexBadge from './DocIndexBadge'
import ApplyTemplateModal from './ApplyTemplateModal'
import { loadGoogleDrive, pickFromDrive, type DriveStatus } from '../../lib/googleDrive'
import {
  DocumentCategoryLabel,
  type AiSummaryResponse,
  type DocumentCategory,
  type DocumentDetail,
  type DocumentListItem,
} from '@toga/shared'

interface Props {
  opportunityId: string
  canWrite: boolean
  onChanged?: () => void
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function DocumentsPanel({ opportunityId, canWrite, onChanged }: Props) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [docs, setDocs] = useState<DocumentListItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [expandedDetail, setExpandedDetail] = useState<DocumentDetail | null>(null)
  const [summarizingId, setSummarizingId] = useState<string | null>(null)
  const [reindexingId, setReindexingId] = useState<string | null>(null)
  // F44 — visor embebido (PDF/imagen/docx/xlsx/texto).
  const [viewer, setViewer] = useState<{ id: string; version: number; fileName: string } | null>(
    null,
  )
  const [showApplyTemplate, setShowApplyTemplate] = useState(false)
  const [aiResult, setAiResult] = useState<{
    title: string
    text: string
    conversationId: string | null
  } | null>(null)
  const [driveStatus, setDriveStatus] = useState<DriveStatus | null>(null)
  const [driveBusy, setDriveBusy] = useState(false)
  const [driveNotConfigured, setDriveNotConfigured] = useState(false)
  const [driveImportErrors, setDriveImportErrors] = useState<string[] | null>(null)

  const reload = useCallback(async () => {
    try {
      const { items } = await api.get<{ items: DocumentListItem[] }>(`/documents?opportunityId=${opportunityId}`)
      setDocs(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('documents.errors.load'))
    }
  }, [opportunityId])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const status = await api.get<DriveStatus>('/integrations/drive/status')
        if (!cancelled) setDriveStatus(status)
      } catch {
        // Si no podemos consultar el estado, simplemente no mostramos el botón.
        if (!cancelled) setDriveStatus({ enabled: false, clientId: null, apiKey: null })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const onImportFromDrive = async () => {
    if (!driveStatus?.enabled || !driveStatus.clientId || !driveStatus.apiKey) return
    setDriveBusy(true)
    setError(null)
    setDriveNotConfigured(false)
    setDriveImportErrors(null)
    try {
      await loadGoogleDrive()
      const { accessToken, files } = await pickFromDrive({
        clientId: driveStatus.clientId,
        apiKey: driveStatus.apiKey,
      })
      if (files.length === 0) {
        setDriveBusy(false)
        return
      }
      const resp = await api.post<{ errors?: Array<{ id?: string; name?: string; error: string }> }>(
        `/opportunities/${opportunityId}/documents/from-drive`,
        { accessToken, files }
      )
      if (resp?.errors && resp.errors.length > 0) {
        setDriveImportErrors(
          resp.errors.map((e) => `${e.name ?? e.id ?? t('documents.file')}: ${e.error}`)
        )
      }
      if (onChanged) onChanged()
      else await reload()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; code?: string } | undefined
        if (err.status === 503 || body?.code === 'DRIVE_NOT_CONFIGURED') {
          setDriveNotConfigured(true)
        } else {
          setError(body?.error ?? err.message)
        }
      } else {
        setError(err instanceof Error ? err.message : t('documents.errors.driveImport'))
      }
    } finally {
      setDriveBusy(false)
    }
  }

  useEffect(() => {
    if (!expandedId) {
      setExpandedDetail(null)
      return
    }
    void (async () => {
      try {
        const { document } = await api.get<{ document: DocumentDetail }>(`/documents/${expandedId}`)
        setExpandedDetail(document)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('documents.errors.loadVersions'))
      }
    })()
  }, [expandedId])

  const onDelete = async (d: DocumentListItem) => {
    if (
      !(await confirmDialog({
        message: t('documents.confirmDelete', { name: d.fileName }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/documents/${d.id}`)
      await reload()
      onChanged?.()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('documents.errors.generic'))
    }
  }

  const onReindex = async (d: DocumentListItem) => {
    setReindexingId(d.id)
    setError(null)
    try {
      await api.post(`/documents/${d.id}/reindex`, {})
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('documents.errors.reindex'))
    } finally {
      setReindexingId(null)
    }
  }

  const onSummarize = async (d: DocumentListItem) => {
    setSummarizingId(d.id)
    setError(null)
    try {
      const resp = await api.post<AiSummaryResponse>('/ai/summary', {
        contextType: 'DOCUMENT',
        contextId: d.id,
        style: 'BRIEF',
      })
      setAiResult({
        title: t('documents.summaryTitle', { name: d.fileName }),
        text: resp.text,
        conversationId: resp.conversationId,
      })
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; code?: string } | undefined
        if (body?.code === 'AI_NOT_CONFIGURED') {
          setError(t('documents.errors.aiNotConfigured'))
        } else {
          setError(body?.error ?? err.message)
        }
      } else {
        setError(err instanceof Error ? err.message : t('documents.errors.summarize'))
      }
    } finally {
      setSummarizingId(null)
    }
  }

  const onDownload = (documentId: string, version: number) => {
    // La API sirve el binario directamente (cookie de sesión, mismo origen):
    // las URLs prefirmadas de MinIO no son resolubles desde el navegador.
    window.open(`/api/documents/${documentId}/versions/${version}/download`, '_blank')
  }

  const handleFiles = async (files: FileList) => {
    if (files.length === 0) return
    const file = files[0]
    if (!file) return
    setUploadOpen(true)
    pendingFileRef.current = file
  }

  const pendingFileRef = useRef<File | null>(null)

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (!canWrite) return
    if (e.dataTransfer.files.length > 0) void handleFiles(e.dataTransfer.files)
  }

  return (
    <div
      className={[
        'surface p-5 relative transition',
        dragOver ? 'ring-2 ring-toga-500/60' : '',
      ].join(' ')}
      onDragOver={(e) => {
        if (!canWrite) return
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <FileText size={16} className="text-toga-700 dark:text-toga-300" />
          {t('documents.title')}
          {docs && <span className="text-xs t-muted font-normal">({docs.length})</span>}
        </h3>
        {canWrite && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setShowApplyTemplate(true)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium t-secondary border border-base hover:bg-hover flex items-center gap-1"
              title={t('documents.templateTooltip')}
            >
              <FileText size={12} /> {t('documents.template')}
            </button>
            {driveStatus?.enabled && (
              <button
                type="button"
                onClick={() => void onImportFromDrive()}
                disabled={driveBusy}
                className="px-2.5 py-1 rounded-lg text-xs font-medium t-secondary border border-base hover:bg-hover flex items-center gap-1 disabled:opacity-50"
                title={t('documents.driveImportTooltip')}
              >
                {driveBusy ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <HardDriveUpload size={12} />
                )}
                {t('documents.importFromDrive')}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                pendingFileRef.current = null
                setUploadOpen(true)
              }}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-700 dark:text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
            >
              <Plus size={14} /> {t('documents.upload')}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 mb-3">
          {error}
        </div>
      )}

      {driveNotConfigured && (
        <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-lg px-2.5 py-1.5 flex items-center gap-2 mb-3">
          <AlertCircle size={12} />
          {t('documents.driveNotConfigured')}
        </div>
      )}

      {driveImportErrors && driveImportErrors.length > 0 && (
        <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-lg px-2.5 py-1.5 flex items-start gap-2 mb-3">
          <AlertCircle size={12} className="mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            <div>{t('documents.someFilesNotImported')}</div>
            <ul className="list-disc pl-4">
              {driveImportErrors.slice(0, 5).map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
              {driveImportErrors.length > 5 && (
                <li>{t('documents.andMore', { count: driveImportErrors.length - 5 })}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {canWrite && (!docs || docs.length === 0) && (
        <div className="text-center py-10 border-2 border-dashed border-base rounded-xl">
          <Upload className="mx-auto t-faint mb-2" size={28} />
          <p className="t-muted text-sm">
            {t('documents.dropzoneHint')}
            <br />
            <span className="text-xs t-faint">{t('documents.maxSize')}</span>
          </p>
        </div>
      )}

      {docs && docs.length > 0 && (
        <ul className="divide-y divide-base/50">
          {docs.map((d) => {
            const isExpanded = expandedId === d.id
            return (
              <li key={d.id} className="py-2.5">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : d.id)}
                    className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted"
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <FileText size={18} className="text-toga-700 dark:text-toga-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="t-primary text-sm font-medium truncate">{d.fileName}</div>
                    <div className="t-faint text-xs flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="px-1.5 py-0.5 rounded bg-[var(--bg-hover)] text-slate-600 dark:text-slate-300">
                        {t('enums:DocumentCategory.' + d.category)}
                      </span>
                      <span>v{d.latestVersion}</span>
                      <span>·</span>
                      <span>{formatSize(d.latestSize)}</span>
                      <DocIndexBadge status={d.indexStatus} />
                      {d.tags.length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <TagIcon size={10} />
                          {d.tags.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void onSummarize(d)}
                    disabled={summarizingId !== null}
                    className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200 disabled:opacity-40"
                    title={t('documents.summarizeAi')}
                  >
                    {summarizingId === d.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Sparkles size={14} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setViewer({ id: d.id, version: d.latestVersion, fileName: d.fileName })
                    }
                    className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200"
                    title={t('documents.view')}
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDownload(d.id, d.latestVersion)}
                    className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200"
                    title={t('documents.downloadLatest')}
                  >
                    <Download size={14} />
                  </button>
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => void onReindex(d)}
                      disabled={reindexingId !== null}
                      className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-700 dark:hover:text-toga-200 disabled:opacity-40"
                      title={t('documents.reindexTooltip')}
                    >
                      {reindexingId === d.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <RefreshCw size={14} />
                      )}
                    </button>
                  )}
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => onDelete(d)}
                      className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                      title={t('common:actions.delete')}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                {isExpanded && (
                  <div className="pl-10 mt-2 mb-1 space-y-1.5">
                    {!expandedDetail ? (
                      <div className="t-muted text-xs flex items-center gap-2">
                        <Loader2 size={12} className="animate-spin" /> {t('documents.loadingVersions')}
                      </div>
                    ) : (
                      <>
                        {expandedDetail.versions.map((v) => (
                          <div key={v.id} className="flex items-center gap-2 text-xs t-muted py-1">
                            <span className="font-mono w-8">v{v.version}</span>
                            <span className="flex-1">
                              {new Date(v.uploadedAt).toLocaleString(currentLocaleTag())} ·{' '}
                              {v.uploadedByName ?? t('documents.unknownUser')}
                            </span>
                            <span>{formatSize(v.size)}</span>
                            <button
                              type="button"
                              onClick={() => onDownload(d.id, v.version)}
                              className="px-2 py-0.5 rounded text-toga-700 dark:text-toga-300 hover:bg-hover"
                            >
                              <Download size={12} />
                            </button>
                          </div>
                        ))}
                        {canWrite && (
                          <UploadNewVersion
                            documentId={d.id}
                            onUploaded={async () => {
                              await reload()
                              const { document } = await api.get<{ document: DocumentDetail }>(
                                `/documents/${d.id}`
                              )
                              setExpandedDetail(document)
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {aiResult && (
        <AiResultModal
          title={aiResult.title}
          text={aiResult.text}
          conversationId={aiResult.conversationId}
          onClose={() => setAiResult(null)}
        />
      )}

      {showApplyTemplate && (
        <ApplyTemplateModal
          opportunityId={opportunityId}
          onClose={() => setShowApplyTemplate(false)}
          onApplied={async () => {
            setShowApplyTemplate(false)
            await reload()
            onChanged?.()
          }}
        />
      )}

      {uploadOpen && (
        <UploadModal
          opportunityId={opportunityId}
          initialFile={pendingFileRef.current}
          onClose={() => {
            setUploadOpen(false)
            pendingFileRef.current = null
          }}
          onUploaded={async () => {
            setUploadOpen(false)
            pendingFileRef.current = null
            await reload()
            onChanged?.()
          }}
        />
      )}

      {viewer && (
        <DocumentViewerModal
          documentId={viewer.id}
          version={viewer.version}
          fileName={viewer.fileName}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  )
}

function UploadNewVersion({
  documentId,
  onUploaded,
}: {
  documentId: string
  onUploaded: () => Promise<void> | void
}) {
  const { t } = useTranslation('opportunities')
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    try {
      const form = new FormData()
      form.append('file', file)
      await api.upload(`/documents/${documentId}/versions`, form)
      await onUploaded()
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | undefined)?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('documents.errors.upload')
      toast.error(msg)
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <label className="text-xs text-toga-700 dark:text-toga-300 hover:underline cursor-pointer inline-flex items-center gap-1">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
      {t('documents.uploadNewVersion')}
      <input ref={inputRef} type="file" className="hidden" onChange={onPick} disabled={busy} />
    </label>
  )
}

function UploadModal({
  opportunityId,
  initialFile,
  onClose,
  onUploaded,
}: {
  opportunityId: string
  initialFile: File | null
  onClose: () => void
  onUploaded: () => Promise<void> | void
}) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [file, setFile] = useState<File | null>(initialFile)
  const [category, setCategory] = useState<DocumentCategory>('OTRO')
  const [tagsRaw, setTagsRaw] = useState('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) return setError(t('documents.errors.selectFile'))
    setError(null)
    setBusy(true)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('opportunityId', opportunityId)
      form.append('category', category)
      if (tagsRaw.trim()) form.append('tags', tagsRaw.trim())
      if (notes.trim()) form.append('notes', notes.trim())
      await api.upload('/documents', form)
      await onUploaded()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(err instanceof Error ? err.message : t('documents.errors.upload'))
      }
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60'

  return (
    <Modal open onClose={onClose} title={t('documents.uploadModalTitle')} maxWidth="max-w-lg">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-xs t-muted mb-1.5">{t('documents.fileLabel')}</label>
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full text-sm t-secondary file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-toga-500/15 file:text-toga-700 dark:file:text-toga-700 dark:text-toga-200 file:cursor-pointer"
          />
          {file && (
            <div className="text-xs t-faint mt-1">
              {file.name} · {formatSize(file.size)}
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs t-muted mb-1.5">{t('documents.categoryLabel')}</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as DocumentCategory)}
            className={inputCls}
          >
            {Object.keys(DocumentCategoryLabel).map((k) => (
              <option key={k} value={k}>
                {t('enums:DocumentCategory.' + k)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs t-muted mb-1.5">{t('documents.tagsLabel')}</label>
          <input
            value={tagsRaw}
            onChange={(e) => setTagsRaw(e.target.value)}
            placeholder={t('documents.tagsPlaceholder')}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1.5">{t('documents.notesLabel')}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className={inputCls}
          />
        </div>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !file}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {t('documents.upload')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
